import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { message } from 'antd';
import Pagination from '../components/common/Pagination';
import * as XLSX from 'xlsx';
import axios from 'axios';
import dayjs from 'dayjs';
import { API_BASE_URL } from '../api/axiosConfig';
const SalesStatus = () => {
    const [orders, setOrders] = useState([]);
    const [aggregatedProducts, setAggregatedProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // 페이지네이션 상태
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [totalCount, setTotalCount] = useState(0);
    // 검색 필터 상태
    const [searchFilters, setSearchFilters] = useState({
        searchTerm: '',
        searchType: 'all',
        startDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
        shippingStatus: ''
    });
    // 빠른 기간 선택
    const [quickPeriod, setQuickPeriod] = useState('');
    // 컴포넌트 마운트 시 데이터 로딩
    useEffect(() => {
        fetchOrders();
    }, []);
    // API로부터 주문 데이터 가져오기 (모든 데이터)
    const fetchOrders = useCallback(async () => {
        var _a, _b, _c, _d, _e;
        setLoading(true);
        setError('');
        try {
            console.log('📡 주문 데이터 API 호출 시작...');
            const response = await axios.get(`${API_BASE_URL}/api/orders/all`, {
                timeout: 15000,
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            console.log('📡 API 응답:', response.data);
            // API 응답 구조 확인 및 데이터 추출
            let ordersData = [];
            if (((_a = response.data) === null || _a === void 0 ? void 0 : _a.success) && ((_b = response.data) === null || _b === void 0 ? void 0 : _b.orders) && Array.isArray(response.data.orders)) {
                ordersData = response.data.orders;
            }
            else if (((_c = response.data) === null || _c === void 0 ? void 0 : _c.data) && Array.isArray(response.data.data)) {
                ordersData = response.data.data;
            }
            else if (Array.isArray(response.data)) {
                ordersData = response.data;
            }
            else {
                throw new Error('API 응답 형식이 올바르지 않습니다.');
            }
            console.log('📊 처리된 주문 데이터:', ordersData.length, '건');
            // 데이터 정렬 (최신순)
            const sortedOrders = ordersData.sort((a, b) => new Date(b.created_at || b.order_date).getTime() - new Date(a.created_at || a.order_date).getTime());
            setOrders(sortedOrders);
            setTotalCount(sortedOrders.length);
            if (sortedOrders.length > 0) {
                message.success(`총 ${sortedOrders.length}건의 판매 데이터를 불러왔습니다.`);
            }
            else {
                message.info('판매 데이터가 없습니다.');
            }
        }
        catch (err) {
            console.error('💥 주문 데이터 로딩 오류:', err);
            if (axios.isAxiosError(err)) {
                if (err.code === 'ECONNABORTED') {
                    setError('서버 응답 시간이 초과되었습니다. 다시 시도해주세요.');
                }
                else if (((_d = err.response) === null || _d === void 0 ? void 0 : _d.status) === 404) {
                    setError('API 엔드포인트를 찾을 수 없습니다.');
                }
                else if (((_e = err.response) === null || _e === void 0 ? void 0 : _e.status) && err.response.status >= 500) {
                    setError('서버 내부 오류가 발생했습니다.');
                }
                else {
                    setError('서버에 연결할 수 없습니다. 네트워크를 확인해주세요.');
                }
            }
            else {
                setError('주문 데이터를 불러오는 중 오류가 발생했습니다.');
            }
            message.error('주문 데이터를 불러오는데 실패했습니다.');
        }
        finally {
            setLoading(false);
        }
    }, []);
    // 상품별 데이터 집계 함수
    const aggregateProductData = useCallback((orders) => {
        const productMap = new Map();
        orders.forEach(order => {
            const key = `${order.product_code}_${order.product_name}`;
            if (productMap.has(key)) {
                const existing = productMap.get(key);
                existing.totalQuantity += order.quantity;
                existing.totalAmount = (existing.totalAmount || 0) + (order.total_amount || 0);
                existing.orderCount += 1;
            }
            else {
                productMap.set(key, {
                    productCode: order.product_code,
                    productName: order.product_name,
                    totalQuantity: order.quantity,
                    unitPrice: order.unit_price,
                    totalAmount: order.total_amount,
                    orderCount: 1
                });
            }
        });
        return Array.from(productMap.values());
    }, []);
    // 클라이언트 사이드 필터링
    const filterOrders = useCallback((orders, filters) => {
        return orders.filter(order => {
            // 검색어 필터링 - 상품코드와 상품명만
            let matchesSearch = true;
            if (filters.searchTerm) {
                if (filters.searchType === 'product_code') {
                    matchesSearch = order.product_code.toLowerCase().includes(filters.searchTerm.toLowerCase());
                }
                else if (filters.searchType === 'product_name') {
                    matchesSearch = order.product_name.toLowerCase().includes(filters.searchTerm.toLowerCase());
                }
                else { // 'all'
                    matchesSearch = order.product_code.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
                        order.product_name.toLowerCase().includes(filters.searchTerm.toLowerCase());
                }
            }
            // 날짜 범위 필터링
            const orderDate = dayjs(order.order_date || order.created_at);
            const matchesDateRange = (!filters.startDate || orderDate.isAfter(dayjs(filters.startDate).subtract(1, 'day'))) &&
                (!filters.endDate || orderDate.isBefore(dayjs(filters.endDate).add(1, 'day')));
            // 배송 상태 필터링
            const matchesShippingStatus = !filters.shippingStatus || order.shipping_status === filters.shippingStatus;
            return matchesSearch && matchesDateRange && matchesShippingStatus;
        });
    }, []);
    // 필터링된 주문 데이터 계산 (useMemo 사용)
    const filteredOrders = useMemo(() => {
        return filterOrders(orders, searchFilters);
    }, [orders, searchFilters, filterOrders]);
    // 필터링된 주문과 합산된 상품 데이터 업데이트
    useEffect(() => {
        const aggregated = aggregateProductData(filteredOrders);
        setAggregatedProducts(aggregated);
        setCurrentPage(1); // 필터 변경 시 첫 페이지로 이동
    }, [filteredOrders, aggregateProductData]);
    // 클라이언트 사이드 페이지네이션 - 합산된 상품 데이터 기준
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentData = itemsPerPage === -1 ? aggregatedProducts : aggregatedProducts.slice(startIndex, endIndex);
    const totalPages = itemsPerPage === -1 ? 1 : Math.ceil(aggregatedProducts.length / itemsPerPage);
    // 빠른 기간 선택 핸들러
    const handleQuickPeriod = (days) => {
        const today = dayjs();
        if (days === 0) {
            setSearchFilters(prev => ({
                ...prev,
                startDate: today.format('YYYY-MM-DD'),
                endDate: today.format('YYYY-MM-DD')
            }));
        }
        else {
            setSearchFilters(prev => ({
                ...prev,
                startDate: today.subtract(days, 'day').format('YYYY-MM-DD'),
                endDate: today.format('YYYY-MM-DD')
            }));
        }
    };
    // 검색 핸들러
    const handleSearch = () => {
        setCurrentPage(1);
        // 검색 필터 상태를 업데이트하여 useMemo 재계산 트리거
        setSearchFilters(prev => ({ ...prev }));
        // 검색 결과 메시지 표시
        const filteredCount = filteredOrders.length;
        if (filteredCount === 0) {
            message.info('검색 결과가 없습니다.');
        }
        else {
            message.success(`${filteredCount}건의 데이터를 찾았습니다.`);
        }
    };
    // 필터 리셋
    const handleReset = () => {
        setSearchFilters({
            searchTerm: '',
            searchType: 'all',
            startDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
            endDate: dayjs().format('YYYY-MM-DD'),
            shippingStatus: ''
        });
        setCurrentPage(1);
    };
    // 페이지 변경 핸들러
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };
    // 공통 Pagination 컴포넌트를 위한 핸들러
    const handleItemsPerPageChange = (itemsPerPage) => {
        setItemsPerPage(itemsPerPage);
        setCurrentPage(1);
    };
    // 엑셀 다운로드 핸들러
    const handleExcelDownload = () => {
        try {
            // 합산된 상품 데이터를 엑셀용으로 변환
            const excelData = aggregatedProducts.map((product, index) => {
                var _a, _b;
                return ({
                    '순번': index + 1,
                    '상품코드': product.productCode,
                    '상품명': product.productName,
                    '총 수량': product.totalQuantity,
                    '단가': ((_a = product.unitPrice) === null || _a === void 0 ? void 0 : _a.toLocaleString()) || 0,
                    '총 금액': ((_b = product.totalAmount) === null || _b === void 0 ? void 0 : _b.toLocaleString()) || 0,
                    '주문 수': product.orderCount
                });
            });
            // 워크시트 생성
            const worksheet = XLSX.utils.json_to_sheet(excelData);
            // 컬럼 너비 설정
            const columnWidths = [
                { wch: 8 },
                { wch: 15 },
                { wch: 30 },
                { wch: 12 },
                { wch: 12 },
                { wch: 12 },
                { wch: 12 } // 주문 수
            ];
            worksheet['!cols'] = columnWidths;
            // 헤더 스타일 적용
            const headerCells = ['A1', 'B1', 'C1', 'D1', 'E1', 'F1', 'G1'];
            headerCells.forEach(cell => {
                if (worksheet[cell]) {
                    worksheet[cell].s = {
                        fill: { fgColor: { rgb: '4472C4' } },
                        font: { color: { rgb: 'FFFFFF' }, bold: true },
                        alignment: { horizontal: 'center' }
                    };
                }
            });
            // 워크북 생성
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, '상품별 판매현황');
            // 다운로드 정보 시트 생성
            const downloadInfo = [
                ['다운로드 정보', ''],
                ['총 상품 수', aggregatedProducts.length],
                ['조회 기간', `${searchFilters.startDate} ~ ${searchFilters.endDate}`],
                ['다운로드 시간', dayjs().format('YYYY-MM-DD HH:mm:ss')],
                ['검색 조건', ''],
                ['검색어', searchFilters.searchTerm || '전체'],
                ['배송상태', searchFilters.shippingStatus || '전체']
            ];
            const infoWorksheet = XLSX.utils.aoa_to_sheet(downloadInfo);
            infoWorksheet['!cols'] = [{ wch: 15 }, { wch: 30 }];
            // 정보 시트 헤더 스타일
            if (infoWorksheet['A1']) {
                infoWorksheet['A1'].s = {
                    fill: { fgColor: { rgb: '70AD47' } },
                    font: { color: { rgb: 'FFFFFF' }, bold: true },
                    alignment: { horizontal: 'center' }
                };
            }
            XLSX.utils.book_append_sheet(workbook, infoWorksheet, '다운로드 정보');
            // 파일명 생성
            let fileName = '상품별판매현황';
            if (searchFilters.startDate && searchFilters.endDate) {
                fileName += `_${searchFilters.startDate}_${searchFilters.endDate}`;
            }
            if (searchFilters.shippingStatus) {
                fileName += `_${searchFilters.shippingStatus}`;
            }
            fileName += `_${dayjs().format('YYYY-MM-DD')}.xlsx`;
            // 파일 다운로드
            XLSX.writeFile(workbook, fileName);
            message.success('엑셀 파일이 다운로드되었습니다.');
        }
        catch (error) {
            console.error('엑셀 다운로드 오류:', error);
            message.error('엑셀 다운로드 중 오류가 발생했습니다.');
        }
    };
    return (_jsx("div", { className: "p-6", children: _jsxs("div", { className: "bg-white rounded-lg shadow-sm", children: [_jsxs("div", { className: "p-6 border-b border-gray-200", children: [_jsxs("div", { className: "flex justify-between items-center mb-6", children: [_jsx("h1", { className: "text-2xl font-bold text-gray-900", children: "\uD83D\uDCCA \uD310\uB9E4\uD604\uD669" }), _jsx("button", { onClick: fetchOrders, disabled: loading, className: "px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500", children: loading ? '로딩 중...' : '🔄 새로고침' })] }), _jsx("div", { className: "mb-4", children: _jsxs("div", { className: "flex items-center space-x-2", children: [_jsx("span", { className: "text-sm font-medium text-gray-700", children: "\uBE60\uB978\uC120\uD0DD:" }), [
                                        { key: 'today', label: '오늘' },
                                        { key: '1week', label: '1주일' },
                                        { key: '1month', label: '1개월' },
                                        { key: '3month', label: '3개월' },
                                        { key: '6month', label: '6개월' },
                                        { key: 'all', label: '전체' }
                                    ].map(period => (_jsx("button", { onClick: () => handleQuickPeriod(period.key === 'today' ? 0 : period.key === '1week' ? 7 : period.key === '1month' ? 30 : period.key === '3month' ? 90 : period.key === '6month' ? 180 : 0), className: `px-3 py-1 text-sm rounded-md ${quickPeriod === period.key
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`, children: period.label }, period.key)))] }) }), _jsx("div", { className: "space-y-4", children: _jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { className: "flex items-center space-x-4", children: [_jsxs("div", { className: "flex items-center space-x-2", children: [_jsx("label", { className: "text-sm font-medium text-gray-700 w-12", children: "\uAC80\uC0C9:" }), _jsxs("select", { value: searchFilters.searchType, onChange: (e) => setSearchFilters(prev => ({ ...prev, searchType: e.target.value })), className: "border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500", children: [_jsx("option", { value: "all", children: "\uC804\uCCB4" }), _jsx("option", { value: "product_code", children: "\uC0C1\uD488\uCF54\uB4DC" }), _jsx("option", { value: "product_name", children: "\uC0C1\uD488\uBA85" })] })] }), _jsx("div", { style: { width: '200px' }, children: _jsx("input", { type: "text", placeholder: "\uC0C1\uD488\uCF54\uB4DC \uB610\uB294 \uC0C1\uD488\uBA85 \uC785\uB825", value: searchFilters.searchTerm, onChange: (e) => setSearchFilters(prev => ({ ...prev, searchTerm: e.target.value })), className: "w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500", onKeyDown: (e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            handleSearch();
                                                        }
                                                    } }) }), _jsxs("div", { className: "flex items-center space-x-2", children: [_jsx("label", { className: "text-sm font-medium text-gray-700", children: "\uAE30\uAC04:" }), _jsx("input", { type: "date", value: searchFilters.startDate, onChange: (e) => setSearchFilters(prev => ({ ...prev, startDate: e.target.value })), className: "border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" }), _jsx("span", { className: "text-gray-500", children: "~" }), _jsx("input", { type: "date", value: searchFilters.endDate, onChange: (e) => setSearchFilters(prev => ({ ...prev, endDate: e.target.value })), className: "border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" })] }), _jsxs("div", { className: "flex items-center space-x-2", children: [_jsx("label", { className: "text-sm font-medium text-gray-700", children: "\uC0C1\uD0DC:" }), _jsxs("select", { value: searchFilters.shippingStatus, onChange: (e) => setSearchFilters(prev => ({ ...prev, shippingStatus: e.target.value })), className: "border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500", children: [_jsx("option", { value: "", children: "\uC804\uCCB4" }), _jsx("option", { value: "\uBC30\uC1A1\uC900\uBE44\uC911", children: "\uBC30\uC1A1\uC900\uBE44\uC911" }), _jsx("option", { value: "\uBC30\uC1A1\uC911", children: "\uBC30\uC1A1\uC911" }), _jsx("option", { value: "\uBC30\uC1A1\uC644\uB8CC", children: "\uBC30\uC1A1\uC644\uB8CC" }), _jsx("option", { value: "\uCDE8\uC18C", children: "\uCDE8\uC18C" })] })] }), _jsxs("div", { className: "flex space-x-2", children: [_jsx("button", { onClick: handleSearch, className: "px-12 py-2 min-w-[100px] whitespace-nowrap bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500", children: "\uAC80\uC0C9" }), _jsx("button", { onClick: handleReset, className: "px-12 py-2 min-w-[100px] whitespace-nowrap bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500", children: "\uCD08\uAE30\uD654" })] })] }), _jsx("div", { className: "flex space-x-2", children: _jsx("button", { onClick: handleExcelDownload, className: "px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500", children: "\uC5D1\uC140\uB2E4\uC6B4\uB85C\uB4DC" }) })] }) }), _jsx("div", { className: "mt-4", children: _jsxs("div", { className: "text-sm text-gray-700", children: ["\uC804\uCCB4 \uC870\uD68C\uC218: ", _jsxs("span", { className: "font-semibold", children: [totalCount, "\uAC74"] }), (searchFilters.searchTerm || searchFilters.startDate || searchFilters.endDate || searchFilters.shippingStatus) && (_jsxs("span", { className: "ml-4", children: ["\uAC80\uC0C9 \uACB0\uACFC: ", _jsxs("span", { className: "font-semibold text-blue-600", children: [filteredOrders.length, "\uAC74"] })] }))] }) }), error && (_jsxs("div", { className: "mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded", children: [_jsx("strong", { children: "\uC624\uB958:" }), " ", error] }))] }), _jsxs("div", { className: "p-6", children: [_jsx("div", { className: "overflow-x-auto shadow-sm border border-gray-200 rounded-lg", children: loading ? (_jsxs("div", { className: "text-center py-12", children: [_jsx("div", { className: "inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" }), _jsx("p", { className: "mt-2 text-gray-500", children: "\uB85C\uB529 \uC911..." })] })) : (_jsxs("table", { className: "min-w-full divide-y divide-gray-200", style: { minWidth: '1200px' }, children: [_jsx("thead", { className: "bg-gray-50", children: _jsxs("tr", { children: [_jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider", children: "\uC21C\uBC88" }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider", children: "\uC0C1\uD488\uCF54\uB4DC" }), _jsx("th", { className: "px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider", children: "\uC0C1\uD488\uBA85" }), _jsx("th", { className: "px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider", children: "\uCD1D \uC218\uB7C9" }), _jsx("th", { className: "px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider", children: "\uB2E8\uAC00" }), _jsx("th", { className: "px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider", children: "\uCD1D \uAE08\uC561" }), _jsx("th", { className: "px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider", children: "\uC8FC\uBB38 \uC218" })] }) }), _jsx("tbody", { className: "bg-white divide-y divide-gray-200", children: currentData.length > 0 ? (currentData.map((product, index) => {
                                            var _a, _b;
                                            return (_jsxs("tr", { className: "hover:bg-gray-50", children: [_jsx("td", { className: "px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900", children: _jsx("code", { className: "bg-gray-100 px-2 py-1 rounded text-xs", children: startIndex + index + 1 }) }), _jsx("td", { className: "px-3 py-3 whitespace-nowrap text-sm text-gray-500", children: _jsx("span", { className: "bg-gray-100 px-2 py-1 rounded text-xs font-mono", children: product.productCode }) }), _jsx("td", { className: "px-3 py-3 text-sm text-gray-500 max-w-xs truncate", children: _jsx("div", { title: product.productName, children: product.productName }) }), _jsx("td", { className: "px-3 py-3 whitespace-nowrap text-sm text-gray-500 text-right", children: _jsx("span", { className: "bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-semibold", children: product.totalQuantity }) }), _jsx("td", { className: "px-3 py-3 whitespace-nowrap text-sm text-gray-500 text-right", children: _jsxs("span", { className: "font-medium", children: [((_a = product.unitPrice) === null || _a === void 0 ? void 0 : _a.toLocaleString()) || 0, "\uC6D0"] }) }), _jsx("td", { className: "px-3 py-3 whitespace-nowrap text-sm text-gray-500 text-right", children: _jsxs("span", { className: "font-semibold text-green-600", children: [((_b = product.totalAmount) === null || _b === void 0 ? void 0 : _b.toLocaleString()) || 0, "\uC6D0"] }) }), _jsx("td", { className: "px-3 py-3 whitespace-nowrap text-sm text-gray-500 text-right", children: _jsxs("span", { className: "font-medium", children: [product.orderCount, "\uAC74"] }) })] }, product.productCode));
                                        })) : (_jsx("tr", { children: _jsx("td", { colSpan: 4, className: "px-6 py-12 text-center text-gray-500", children: filteredOrders.length === 0 && orders.length > 0 ? (_jsxs("div", { children: [_jsx("p", { className: "mb-1", children: "\uAC80\uC0C9 \uC870\uAC74\uC5D0 \uB9DE\uB294 \uB370\uC774\uD130\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4." }), _jsx("small", { className: "text-gray-400", children: "\uB2E4\uB978 \uAC80\uC0C9 \uC870\uAC74\uC744 \uC2DC\uB3C4\uD574\uBCF4\uC138\uC694." })] })) : (_jsxs("div", { children: [_jsx("p", { className: "mb-1", children: "\uB370\uC774\uD130\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4." }), _jsx("small", { className: "text-gray-400", children: "\uC0C8\uB85C\uACE0\uCE68 \uBC84\uD2BC\uC744 \uD074\uB9AD\uD558\uC5EC \uB370\uC774\uD130\uB97C \uB2E4\uC2DC \uBD88\uB7EC\uC624\uC138\uC694." })] })) }) })) })] })) }), _jsx(Pagination, { currentPage: currentPage, totalPages: totalPages, itemsPerPage: itemsPerPage, totalItems: filteredOrders.length, onPageChange: handlePageChange, onItemsPerPageChange: handleItemsPerPageChange })] })] }) }));
};
export default SalesStatus;
